import {
  createMissingEntityTargetTitle,
  createMissingLoreTargetTitle,
  getEntityCardHref,
  getEntityLoreHref,
} from "@/features/entities";
import {
  getTimelineChapterHref,
  getTimelineLinkKindLabelKey,
  type TimelineEventLinkView,
  type TimelineEventView,
  type TimelineLinkTargetOption,
  type TimelineWorkspaceSnapshot,
} from "@/features/timeline";
import {
  loadTimelineWorkspaceSource,
  type TimelineChapterRow,
  type TimelineEntityTargetRow,
  type TimelineLoreTargetRow,
  type TimelineStoryRow,
  type TimelineWorkspaceSource,
} from "@/features/timeline/timeline.client";
import { createTimelineEventImageSignedUrl } from "@/features/timeline/timelineStorage";

function formatTypeLabel(value: string) {
  return value
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function createDescriptionPreview(value: string, maxLength = 148) {
  const collapsed = value.replace(/\s+/g, " ").trim();

  if (collapsed.length <= maxLength) {
    return collapsed;
  }

  return `${collapsed.slice(0, maxLength - 1).trimEnd()}...`;
}

function buildChapterSecondaryText(chapter: TimelineChapterRow, story: TimelineStoryRow | undefined) {
  if (!story) {
    return `Chapter ${chapter.sort_order + 1}`;
  }

  return `${story.title} / Chapter ${chapter.sort_order + 1}`;
}

function buildTimelineLinkOptions(source: TimelineWorkspaceSource): TimelineLinkTargetOption[] {
  const storyRowsById = new Map(source.storyRows.map((story) => [story.id, story]));

  const entityOptions = source.entityRows.map((row) => ({
    id: row.id,
    kind: "entity" as const,
    kindLabelKey: getTimelineLinkKindLabelKey("entity"),
    label: row.name,
    secondaryText: formatTypeLabel(row.type),
  }));

  const loreOptions = source.loreRows.map((row) => ({
    id: row.id,
    kind: "lore" as const,
    kindLabelKey: getTimelineLinkKindLabelKey("lore"),
    label: row.title,
    secondaryText: `${formatTypeLabel(row.type)} / ${formatTypeLabel(row.canon_status)}`,
  }));

  const chapterOptions = source.chapterRows.map((row) => ({
    id: row.id,
    kind: "chapter" as const,
    kindLabelKey: getTimelineLinkKindLabelKey("chapter"),
    label: row.title,
    secondaryText: buildChapterSecondaryText(row, storyRowsById.get(row.story_id)),
  }));

  return [...entityOptions, ...loreOptions, ...chapterOptions];
}

function buildMissingChapterTitle(targetId: string) {
  return `Missing Chapter (${targetId})`;
}

function buildLinkView(
  link: TimelineWorkspaceSource["linkRows"][number],
  projectId: string,
  entityRowsById: Map<string, TimelineEntityTargetRow>,
  loreRowsById: Map<string, TimelineLoreTargetRow>,
  chapterRowsById: Map<string, TimelineChapterRow>,
  storyRowsById: Map<string, TimelineStoryRow>,
): TimelineEventLinkView {
  if (link.linked_kind === "entity") {
    const entity = entityRowsById.get(link.linked_id);

    return entity
      ? {
          broken: false,
          id: link.id,
          label: entity.name,
          linkedId: link.linked_id,
          linkedKind: "entity",
          linkedKindLabelKey: getTimelineLinkKindLabelKey("entity"),
          openHref: getEntityCardHref(projectId, entity.id),
          repairHintKey: null,
          secondaryText: formatTypeLabel(entity.type),
        }
      : {
          broken: true,
          id: link.id,
          label: createMissingEntityTargetTitle(link.linked_id),
          linkedId: link.linked_id,
          linkedKind: "entity",
          linkedKindLabelKey: getTimelineLinkKindLabelKey("entity"),
          openHref: null,
          repairHintKey: "timeline.links.unavailable",
          secondaryText: "Unavailable",
        };
  }

  if (link.linked_kind === "lore") {
    const lore = loreRowsById.get(link.linked_id);

    return lore
      ? {
          broken: false,
          id: link.id,
          label: lore.title,
          linkedId: link.linked_id,
          linkedKind: "lore",
          linkedKindLabelKey: getTimelineLinkKindLabelKey("lore"),
          openHref: getEntityLoreHref(projectId, lore.id),
          repairHintKey: null,
          secondaryText: `${formatTypeLabel(lore.type)} / ${formatTypeLabel(lore.canon_status)}`,
        }
      : {
          broken: true,
          id: link.id,
          label: createMissingLoreTargetTitle(link.linked_id),
          linkedId: link.linked_id,
          linkedKind: "lore",
          linkedKindLabelKey: getTimelineLinkKindLabelKey("lore"),
          openHref: null,
          repairHintKey: "timeline.links.unavailable",
          secondaryText: "Unavailable",
        };
  }

  const chapter = chapterRowsById.get(link.linked_id);
  const story = chapter ? storyRowsById.get(chapter.story_id) : undefined;

  return chapter
    ? {
        broken: false,
        id: link.id,
        label: chapter.title,
        linkedId: link.linked_id,
        linkedKind: "chapter",
        linkedKindLabelKey: getTimelineLinkKindLabelKey("chapter"),
        openHref: getTimelineChapterHref(projectId, chapter.id),
        repairHintKey: null,
        secondaryText: buildChapterSecondaryText(chapter, story),
      }
    : {
        broken: true,
        id: link.id,
        label: buildMissingChapterTitle(link.linked_id),
        linkedId: link.linked_id,
        linkedKind: "chapter",
        linkedKindLabelKey: getTimelineLinkKindLabelKey("chapter"),
        openHref: null,
        repairHintKey: "timeline.links.unavailable",
        secondaryText: "Unavailable",
      };
}

async function buildTimelineEventViews(source: TimelineWorkspaceSource): Promise<TimelineEventView[]> {
  const entityRowsById = new Map(source.entityRows.map((row) => [row.id, row]));
  const loreRowsById = new Map(source.loreRows.map((row) => [row.id, row]));
  const chapterRowsById = new Map(source.chapterRows.map((row) => [row.id, row]));
  const storyRowsById = new Map(source.storyRows.map((row) => [row.id, row]));
  const linkRowsByEventId = source.linkRows.reduce<Map<string, TimelineEventLinkView[]>>(
    (collection, link) => {
      const nextLinks = collection.get(link.event_id) ?? [];
      nextLinks.push(
        buildLinkView(
          link,
          source.project.id,
          entityRowsById,
          loreRowsById,
          chapterRowsById,
          storyRowsById,
        ),
      );
      collection.set(link.event_id, nextLinks);
      return collection;
    },
    new Map(),
  );

  return Promise.all(
    source.eventRows.map(async (row) => {
      const links = linkRowsByEventId.get(row.id) ?? [];
      const brokenLinkCount = links.filter((link) => link.broken).length;
      const visibleSummary = source.capacity.tier === "free" ? null : row.ai_summary;
      const imageUrl = row.image_path
        ? await createTimelineEventImageSignedUrl(row.image_path).catch(() => null)
        : null;

      return {
        aiSummary: visibleSummary,
        aiSummaryGeneratedAt: row.ai_summary_generated_at,
        brokenLinkCount,
        createdAt: row.created_at,
        description: row.description,
        descriptionPreview: createDescriptionPreview(row.description),
        eraLabel: row.era_label,
        hasApprovedSummary: Boolean(row.ai_summary),
        id: row.id,
        imagePath: row.image_path,
        imageUrl,
        linkCount: links.length,
        links,
        projectId: row.project_id,
        sortOrder: row.sort_order,
        title: row.title,
        updatedAt: row.updated_at,
      } satisfies TimelineEventView;
    }),
  );
}

export async function buildTimelineWorkspaceSnapshot(
  source: TimelineWorkspaceSource,
): Promise<TimelineWorkspaceSnapshot> {
  return {
    eventCapacity: source.capacity,
    events: await buildTimelineEventViews(source),
    linkOptions: buildTimelineLinkOptions(source),
    projectId: source.project.id,
    projectTitle: source.project.title,
    tier: source.capacity.tier,
  };
}

export async function loadTimelineWorkspace(projectId: string) {
  const source = await loadTimelineWorkspaceSource(projectId);
  return await buildTimelineWorkspaceSnapshot(source);
}
